import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { getTouchpointComment } from "generated/api/touchpoint-comment";
import type {
  CreateCommentVm,
  CreateCommentReplyVm,
  GetAllCommentsForTouchpointParams,
  UpdateCommentVm,
} from "generated/schemas";

// The comment list endpoint exposes its criteria and pagination as flattened query
// params (orval `@ParameterObject`). Derive the criteria / pageable shapes from it.
type CommentCriteria = Pick<GetAllCommentsForTouchpointParams, "hidden" | "actionItem" | "open">;
type Pageable = Pick<GetAllCommentsForTouchpointParams, "page" | "size" | "sort">;

import {
  mapCommentDTO,
  mapCommentReplyDTO,
  normalizeCommentPage,
  normalizeCommentReplyPage,
} from "utilities/api-mappers";

import type { Comment, CommentReply, CommentsSummary } from "types/comments";
import type { PaginatedResponse } from "types/pagination";

import { commentKeys } from "./query-keys";

const commentApi = getTouchpointComment();

export type TouchpointCommentScope = {
  clientId: string;
  campaignId: string;
  touchpointId: string;
};

/**
 * Parses the legacy "filter=open=true" style filter string used by
 * `commentFilterOptions` into the Orval `CommentCriteria` shape.
 * Accepts comma-, semicolon- or ampersand-separated key=value pairs.
 */
export function parseCommentFilter(filter?: string): CommentCriteria {
  if (!filter) {
    return {};
  }
  const criteria: CommentCriteria = {};
  filter
    .split(/[;&,]/)
    .map((token) => token.trim())
    .filter(Boolean)
    .forEach((token) => {
      const [rawKey, rawValue] = token.split("=");
      if (!rawKey) return;
      const value = rawValue?.trim();
      switch (rawKey.trim()) {
        case "open":
          criteria.open = value === "true";
          break;
        case "hidden":
          criteria.hidden = value === "true";
          break;
        case "actionItem":
          criteria.actionItem = value === "true";
          break;
        default:
          break;
      }
    });
  return criteria;
}

export type LoadCommentsOptions = {
  filter?: string;
  pageable?: Pageable;
  additionalCriteria?: CommentCriteria;
};

/**
 * Helper: load a paged list of comments. Stays imperative on purpose so that
 * the existing `comment-context` can drive its own pagination.
 */
export async function loadComments(
  scope: TouchpointCommentScope,
  options: LoadCommentsOptions = {},
): Promise<PaginatedResponse<Comment>> {
  const criteria: CommentCriteria = {
    ...parseCommentFilter(options.filter),
    ...options.additionalCriteria,
  };
  const response = await commentApi.getAllCommentsForTouchpoint(scope, {
    ...criteria,
    ...(options.pageable ?? {}),
  });
  return normalizeCommentPage(response);
}

export async function loadCommentSummary(
  scope: TouchpointCommentScope,
  hiddenFromClient?: boolean,
): Promise<CommentsSummary> {
  const criteria: CommentCriteria = {};
  if (hiddenFromClient !== undefined) {
    criteria.hidden = hiddenFromClient;
  }
  const response = await commentApi.getCommentSummary(scope, { ...criteria });
  return {
    commentCount: response.commentCount ?? 0,
    actionItemCount: response.actionItemCount ?? 0,
    hiddenCount: response.hiddenCount ?? 0,
    openCount: response.openCount ?? 0,
  };
}

export async function createCommentRequest(
  scope: TouchpointCommentScope,
  payload: CreateCommentVm,
): Promise<Comment> {
  const dto = await commentApi.createComment(scope, payload);
  return mapCommentDTO(dto);
}

export async function updateCommentRequest(
  scope: TouchpointCommentScope,
  id: string,
  payload: UpdateCommentVm,
): Promise<Comment> {
  const dto = await commentApi.updateComment({ ...scope, id }, payload);
  return mapCommentDTO(dto);
}

export async function loadCommentReplies(
  scope: TouchpointCommentScope,
  commentId: string,
  pageable: Pageable = {},
): Promise<PaginatedResponse<CommentReply>> {
  const response = await commentApi.getCommentReplies(
    { ...scope, id: commentId },
    { ...pageable },
  );
  return normalizeCommentReplyPage(response);
}

export async function createCommentReplyRequest(
  scope: TouchpointCommentScope,
  parentId: string,
  payload: CreateCommentReplyVm,
): Promise<CommentReply> {
  const dto = await commentApi.replyToComment(
    { ...scope, id: parentId },
    payload,
  );
  return mapCommentReplyDTO(dto);
}

/**
 * `loadComments` behind the query cache, for callers that drive their own pagination and so cannot use
 * `useQuery`. Only the in-flight request is shared: `staleTime: 0` keeps every call asking the server, so
 * nothing is served stale and the only thing that changes is that two callers firing the same request at
 * once get one round trip. In practice that is `<React.StrictMode>` (src/index.tsx) replaying a mount
 * effect. `retry` stays off so one failed load does not become three requests.
 */
export const useLoadTouchpointComments = (scope: TouchpointCommentScope) => {
  const queryClient = useQueryClient();
  const { clientId, campaignId, touchpointId } = scope;

  return (options: LoadCommentsOptions = {}) =>
    queryClient.fetchQuery({
      queryKey: commentKeys.list(clientId, campaignId, touchpointId, { ...options }),
      queryFn: () => loadComments({ clientId, campaignId, touchpointId }, options),
      staleTime: 0,
      retry: false,
    });
};

export const useCommentReplies = (
  scope: TouchpointCommentScope,
  commentId: string | undefined,
  options: { enabled?: boolean } = {},
) =>
  useQuery({
    queryKey: commentKeys.replies(
      scope.clientId,
      scope.campaignId,
      scope.touchpointId,
      commentId ?? "",
    ),
    queryFn: () => {
      if (!commentId) {
        throw new Error("Comment id required for useCommentReplies queryFn");
      }

      return loadCommentReplies(scope, commentId, { sort: ["createdDate,asc"] });
    },
    enabled: Boolean(commentId) && (options.enabled ?? true),
  });

/**
 * Writes a just-posted reply straight into the cached thread instead of invalidating, so the open
 * thread grows without a second round trip. A no-op until the thread has been fetched once — a first
 * reply lands via the refetch that `replyCount` going 0 -> 1 triggers at the call site.
 */
export const useAppendCommentReply = (scope: TouchpointCommentScope, commentId?: string) => {
  const queryClient = useQueryClient();

  return (reply: CommentReply) => {
    if (!commentId) {
      return;
    }

    queryClient.setQueryData<PaginatedResponse<CommentReply>>(
      commentKeys.replies(scope.clientId, scope.campaignId, scope.touchpointId, commentId),
      (current) =>
        current && {
          ...current,
          items: [...current.items, reply],
          totalItems: current.totalItems + 1,
        },
    );
  };
};

/**
 * Invalidate every cached comment query for the given touchpoint. Useful when
 * an action elsewhere mutates a comment.
 */
export const useInvalidateTouchpointComments = (scope: TouchpointCommentScope) => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: commentKeys.scope(scope.clientId, scope.campaignId, scope.touchpointId),
    });
};

export const useDeleteTouchpointComment = (scope: TouchpointCommentScope) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => commentApi.deleteComment({ ...scope, id }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: commentKeys.scope(scope.clientId, scope.campaignId, scope.touchpointId),
      });
    },
  });
};
